// /lib/logger.ts
import { Browser as LogtailBrowser, Node as LogtailNode } from '@logtail/js';
import { v4 as uuidv4 } from 'uuid';

// Initialize environment variables - only validate in production
const LOGTAIL_ENDPOINT =
  process.env.NEXT_PUBLIC_LOGTAIL_INGESTING_HOST ??
  (process.env.NODE_ENV !== 'development'
    ? (() => {
        throw new Error(
          'Missing required environment variable: NEXT_PUBLIC_LOGTAIL_INGESTING_HOST',
        );
      })()
    : '');
const LOGTAIL_SOURCE_TOKEN =
  process.env.NEXT_PUBLIC_LOGTAIL_SOURCE_TOKEN ??
  (process.env.NODE_ENV !== 'development'
    ? (() => {
        throw new Error(
          'Missing required environment variable: NEXT_PUBLIC_LOGTAIL_SOURCE_TOKEN',
        );
      })()
    : '');

// Types for our logger
export type LogLevel = 'error' | 'warn' | 'info' | 'debug';

// Interface for the session context
export interface SessionContext {
  userId?: string;
  sessionId: string;
  userAgent?: string;
  path?: string;
}

// Create the client-side logger
export const createClientLogger = () => {
  // Initialize the Logtail client for browser environment (or use a mock in dev without credentials)
  const logtailClient = LOGTAIL_SOURCE_TOKEN
    ? new LogtailBrowser(LOGTAIL_SOURCE_TOKEN, {
        endpoint: LOGTAIL_ENDPOINT,
        sendLogsToConsoleOutput: false, // We'll handle console output manually
      })
    : {
        // Mock implementation when no token is provided
        log: (message: string) => {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[MOCK LOGTAIL]', message);
          }
        },
      };

  // Initialize session context - we'll use this to track user sessions
  let sessionContext: SessionContext = {
    sessionId: uuidv4(), // Generate a new session ID
  };

  // Default metadata for logs (only used for Logtail, not console output)
  let defaultMeta: Record<string, unknown> = {
    service: 'client-side',
    ...sessionContext,
  };

  /**
   * Initialize the logger with user session information
   * @param context The session context information
   */
  const initLogger = (context: Partial<SessionContext> = {}): void => {
    // Update the session context with any new information
    sessionContext = {
      ...sessionContext,
      ...context,
    };

    // Update the default metadata for all log messages
    defaultMeta = {
      ...defaultMeta,
      ...sessionContext,
    };
  };

  /**
   * Log a message with the specified level
   * @param level The log level
   * @param message The message to log
   * @param meta Additional metadata to include
   */
  const log = (
    level: LogLevel,
    message: string = '',
    meta: Record<string, unknown> = {},
  ): void => {
    // For Logtail, include all context and metadata
    const logtailContext = {
      ...defaultMeta,
      ...meta,
      timestamp: new Date().toISOString(),
      level,
    };

    // Send to Logtail with full context
    logtailClient.log(message, JSON.stringify(logtailContext));

    // For console, only include the provided metadata to keep logs clean
    // Only log to console in non-production environments
    if (process.env.NODE_ENV === 'development') {
      // Respect LOG_LEVEL environment variable - for Next.js, we need NEXT_PUBLIC_ prefix for client
      const currentLogLevel = (
        process.env.NEXT_PUBLIC_LOG_LEVEL ||
        process.env.LOG_LEVEL ||
        'info'
      ).toLowerCase();
      // Temporary debug line - remove after fixing
      const logLevels = { error: 0, warn: 1, info: 2, debug: 3 };

      if (
        logLevels[level as keyof typeof logLevels] <=
        logLevels[currentLogLevel as keyof typeof logLevels]
      ) {
        // Map log levels to console methods
        const logMethodMap = {
          error: console.error,
          warn: console.warn,
          info: console.info,
          debug: console.debug,
        };

        const consoleMethod = logMethodMap[level as keyof typeof logMethodMap];

        // Only log the message and explicitly provided metadata to keep console clean
        if (Object.keys(meta)?.length > 0) {
          consoleMethod(`[${level.toUpperCase()}] ${message}`, meta);
        } else {
          consoleMethod(`[${level.toUpperCase()}] ${message}`);
        }
      }
    }
  };

  // Convenience methods for different log levels
  const logError = (
    message: string | undefined,
    meta: Record<string, unknown> = {},
  ): void => log('error', message, meta);
  const logWarn = (
    message: string | undefined,
    meta: Record<string, unknown> = {},
  ): void => log('warn', message, meta);
  const logInfo = (
    message: string | undefined,
    meta: Record<string, unknown> = {},
  ): void => log('info', message, meta);
  const logDebug = (
    message: string | undefined,
    meta: Record<string, unknown> = {},
  ): void => log('debug', message, meta);

  // Return the logger instance and helper methods
  return {
    init: initLogger,
    log,
    error: logError,
    warn: logWarn,
    info: logInfo,
    debug: logDebug,
    defaultMeta,
  };
};

// For server components, create a proper server-side logger
export const createServerLogger = () => {
  // Initialize the Logtail client for Node.js environment (or use a mock in dev without credentials)
  const logtailClient = LOGTAIL_SOURCE_TOKEN
    ? new LogtailNode(LOGTAIL_SOURCE_TOKEN, {
        endpoint: LOGTAIL_ENDPOINT,
      })
    : {
        // Mock implementation when no token is provided
        log: (message: string) => {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[MOCK LOGTAIL]', message);
          }
        },
      };

  // Initialize session context
  let sessionContext: SessionContext = {
    sessionId: uuidv4(),
  };

  // Default metadata for logs
  let defaultMeta: Record<string, unknown> = {
    service: 'server-side',
    ...sessionContext,
  };

  const initLogger = (context: Partial<SessionContext> = {}): void => {
    sessionContext = {
      ...sessionContext,
      ...context,
    };

    defaultMeta = {
      ...defaultMeta,
      ...sessionContext,
    };
  };

  const log = (
    level: LogLevel,
    message: string = '',
    meta: Record<string, unknown> = {},
  ): void => {
    // For Logtail, include all context and metadata
    const logtailContext = {
      ...defaultMeta,
      ...meta,
      timestamp: new Date().toISOString(),
      level,
    };

    // Send to Logtail with full context
    logtailClient.log(message, JSON.stringify(logtailContext));

    // Respect LOG_LEVEL environment variable - need NEXT_PUBLIC_ prefix in Next.js
    const currentLogLevel = (
      process.env.NEXT_PUBLIC_LOG_LEVEL ||
      process.env.LOG_LEVEL ||
      'info'
    ).toLowerCase();
    // Temporary debug line - remove after fixing
    const logLevels = { error: 0, warn: 1, info: 2, debug: 3 };

    if (
      logLevels[level as keyof typeof logLevels] <=
      logLevels[currentLogLevel as keyof typeof logLevels]
    ) {
      // Map log levels to console methods
      const logMethodMap = {
        error: console.error,
        warn: console.warn,
        info: console.info,
        debug: console.debug,
      };

      const consoleMethod = logMethodMap[level as keyof typeof logMethodMap];

      // Only log the message and explicitly provided metadata to keep console clean
      if (Object.keys(meta)?.length > 0) {
        consoleMethod(`[${level.toUpperCase()}] ${message}`, meta);
      } else {
        consoleMethod(`[${level.toUpperCase()}] ${message}`);
      }
    }
  };

  // Convenience methods for different log levels
  const logError = (
    message: string | undefined,
    meta: Record<string, unknown> = {},
  ): void => log('error', message, meta);
  const logWarn = (
    message: string | undefined,
    meta: Record<string, unknown> = {},
  ): void => log('warn', message, meta);
  const logInfo = (
    message: string | undefined,
    meta: Record<string, unknown> = {},
  ): void => log('info', message, meta);
  const logDebug = (
    message: string | undefined,
    meta: Record<string, unknown> = {},
  ): void => log('debug', message, meta);

  return {
    init: initLogger,
    log,
    error: logError,
    warn: logWarn,
    info: logInfo,
    debug: logDebug,
    defaultMeta,
  };
};

// Determine if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Export a singleton instance that's safe in both environments
const logger = isBrowser ? createClientLogger() : createServerLogger();

export { logger };
